import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PageHeading from "components/PageHeading";
import { API } from "api";
import { UTILS } from "utils";
import { PackageUtil } from "utils";
import Modal from "components/Modal";
import useCatalogue from "hooks/useCatalogue";
import ModalSelectStore from "../components/ModalSelectStore";
import AppStep from "components/ui/AppStep.jsx";
import AppIcon from "components/ui/AppIcon.jsx";
import { useMatchMediaDimensions } from "hooks/useWindowDimensions";
import ShipmentDelivery from "features/shipping_methods/containers/ShippingDelivery";
import BrandIdentity from "components/features/store/brand-identity";
import CreateMenu from "components/features/store/create-menu";
import AddProducts from "components/features/store/products";
import LaunchStore from "components/features/store/launch-store";
import PreviewStore from "components/features/store/preview-store";
import StoreViewController from "features/store/containers/viewController";  
import { Spin } from "antd";

const GetStarted = () => {
  const history = useHistory();
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState("shop");
  const [furthestStepIndex, setFurthestStepIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [videoId, setVideoId] = useState("");
  const [modalVideoOpen, setModalVideoOpen] = useState(false);
  const { t } = useTranslation();
  const isMobile = useMatchMediaDimensions(768);

  const defaultSteps = [
    {
      title: "Brand Identity",
      icon: "shop",
      activeKey: "shop",
      completed: false,
      mapKey: "CUSTOMIZE_BRANDING",
      content:
        "Personalize your store with your brand, visuals, and a unique tagline.",
    },
    {
      title: "Create Store",
      icon: "user",
      activeKey: "create",
      completed: false,
      mapKey: "CREATE_STORE",
      content: "Set up your store to start receiving and preparing orders.",
    },
    {
      title: "Delivery Zones",
      icon: "delivery",
      activeKey: "delivery",
      completed:false,
      mapKey:"ADD_DELIVERY_AREA",
      content:
        "Choose areas where your store can deliver within product shelf life.",
    },

    {
      title: "Add Products",
      icon: "shopAdd",
      activeKey: "products",
      mapKey: "CREATE_ITEM",
      content:
        "Create items now—link to categories later and control visibility per store.",
    },
    // {
    //   title: 'Create Category',
    //   icon:'note',
    //   activeKey:"category",
    //   content:"Organize your brand with categories and manage items per store.",
    // },
    {
      title: "Create Menu",
      icon: "reserve",
      activeKey: "menu",
      completed: false,
      mapKey: "CREATE_CATEGORY",
      content: "Set the web address where customers will find your store.",
    },
    {
      title: "Launch Store",
      icon: "rocket2",
      activeKey: "launch",
      completed: false,
      mapKey: "PUBLISH_STORE",
      content: "Ready to launch? Publish your store and welcome customers.",
    },
    {
      title: "Preview Store",
      icon: "eye",
      activeKey: "preview",
      completed: false,
      mapKey: "PREVIEW_STORE",
      content: "Preview your store as customers will see it.",
    },
  ];
  const catelougeType = useCatalogue("getstarted");
  const mappedSteps = useMemo(() => {
    const statusByKey = new Map();

    if (Array.isArray(steps)) {
      steps.forEach((apiStep) => {
        const apiKey =
          apiStep?.mapKey ||
          apiStep?.taskKey ||
          apiStep?.guidanceTaskKey ||
          apiStep?.key ||
          apiStep?.taskType;
        if (!apiKey) return;
        statusByKey.set(String(apiKey).toUpperCase(), apiStep);
      });
    }

    return defaultSteps.map((step) => {
      const apiStep = statusByKey.get(String(step.mapKey).toUpperCase());
      const isCompleted = Boolean(
        apiStep?.isCompleted ?? apiStep?.completed ?? step.completed
      );

      return {
        ...step,
        completed: isCompleted,
        isCompleted,
      };
    });
  }, [steps]);
  const stepKeys = useMemo(
    () => mappedSteps.map((step) => step.activeKey),
    [mappedSteps]
  );
  const currentStepIndex = useMemo(
    () => mappedSteps.findIndex((step) => step.activeKey === currentStep),
    [mappedSteps, currentStep]
  );
  const completedByProgress = React.useCallback(
    (index) => index <= furthestStepIndex,
    [furthestStepIndex]
  );
  const canNavigateToStep = React.useCallback(
    (index, step) => {
      if (index <= currentStepIndex) return true;
      return Boolean(step?.isCompleted);
    },
    [currentStepIndex]
  );
  const getGuidanceFlowSetupStatus = async () => {
    setLoading(true);
    const userDetail = UTILS.getCurrentUser();
    const result = await API.GET_GUIDANCE_FLOW_SETUP_WITH_STATUS({
      token: userDetail.token,
    });
    if (result?.success === 1 && Array.isArray(result.result))
      setSteps(result?.result);
    else {
    }
    setLoading(false);
  };

  const playVideo = (id) => {
    setVideoId(id);
    setModalVideoOpen(true);
  };

  const addTaskToDone = async (id) => {
    const userDetail = UTILS.getCurrentUser();
    const result = await API.ADD_TASK_TO_DONE({
      token: userDetail.token,
      taskId: id,
    });
    getGuidanceFlowSetupStatus();
  };
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);
  /** `undefined` until first fetch — avoids showing the create form then swapping. */
  const [hasExistingStore, setHasExistingStore] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    const loadStoresPresence = async () => {
      const userDetail = UTILS.getCurrentUser();
      if (!userDetail?.token) {
        if (!cancelled) setHasExistingStore(false);
        return;
      }
      const result = await API.GET_CRRENT_USER_STORES({
        token: userDetail.token,
        language: currentLanguage ?? "EN",
      });
      if (cancelled) return;
      setHasExistingStore(
        result?.success === 1 &&
          Array.isArray(result.data) &&
          result.data.length >= 1
      );
    };
    loadStoresPresence();
    return () => {
      cancelled = true;
    };
  }, [currentLanguage]);

  const check_PackageValidity = async () => {
    const { result } = await UTILS.checkPackageValidity(currentLanguage);
    if (result?.data) {
      const diff = PackageUtil.getPackageExpiry(result.data.subscription);
      if (diff > 5) {
        history.push("/app/dashboard/subscription");
      }
    }
  };
  useEffect(() => {}, []);

  useEffect(() => {
    check_PackageValidity();
    getGuidanceFlowSetupStatus();
  }, []);

  useEffect(() => {
    if (!Array.isArray(mappedSteps) || mappedSteps.length === 0) return;
    const firstPendingStep = mappedSteps.find((step) => !step.isCompleted);
    const lastCompletedIndex = mappedSteps.reduce(
      (last, step, idx) => (step.isCompleted ? idx : last),
      -1
    );
    setFurthestStepIndex((prev) => Math.max(prev, lastCompletedIndex, 0));
    setCurrentStep((prev) => {
      const selectedStep = mappedSteps.find((step) => step.activeKey === prev);
      if (selectedStep && !selectedStep.isCompleted) {
        return prev;
      }
      return firstPendingStep?.activeKey || mappedSteps[0]?.activeKey || "shop";
    });
  }, [mappedSteps]);

  useEffect(() => {
    if (currentStepIndex < 0) return;
    setFurthestStepIndex((prev) => Math.max(prev, currentStepIndex));
  }, [currentStepIndex]);

  const goToNextStep = React.useCallback(() => {
    setCurrentStep((prev) => {
      const currentIndex = stepKeys.findIndex((key) => key === prev);
      if (currentIndex === -1) return stepKeys[0] || "shop";
      const nextKey = stepKeys[currentIndex + 1];
      return nextKey || prev;
    });
  }, [stepKeys]);

  const getEnabled = (s, index) => {
    if (s?.isCompleted) return "enabled";
    else if (index == 0) return "enabled";
    else if (steps[index - 1]?.isCompleted && !s?.isCompleted) return "enabled";
    else return "disabled";
  };

  const getOpened = (s, index) => {
    if (index === 0 && !s.isCompleted) return true;
    else if (steps[index - 1]?.isCompleted && !s?.isCompleted) return true;
    else return false;
  };

  /** Same as AppContainer — use theme keys (Tailwind v2 without JIT ignores `h-[calc(...)]` arbitrary classes). */
  const onboardingPanelH =
    "md:h-onboarding-card md:max-h-onboarding-card md:min-h-0";
  /** Fixed shell height so inner list scrolls; md matches main AppContainer card. */
  const stepsCardShell =
    "flex min-h-0 w-full flex-col overflow-hidden rounded-3xl bg-white shadow-primary-shadow max-md:h-onboarding-steps-mobile max-md:max-h-onboarding-steps-mobile md:h-onboarding-card md:max-h-onboarding-card";

  const StepsComponentHasMap = {
    shop: BrandIdentity,
    menu: CreateMenu,
    products: AddProducts,
    delivery: ShipmentDelivery,
    launch: LaunchStore,
    preview: PreviewStore,
  };

  const renderStepContent = () => {
    if (currentStep === "create") {
      if (hasExistingStore === undefined) {
        return (
          <div className="flex min-h-[240px] flex-1 items-center justify-center rounded-3xl bg-white shadow-primary-shadow">
            <Spin size="large" />
          </div>
        );
      }
      return <StoreViewController as="component" defaultMode={hasExistingStore ? "list" : "create"} onSaveAndContinue={goToNextStep} title="Manage Stores" subTitle="launch_store_dashboard_list_hint" />

    }
    const Comp = StepsComponentHasMap[currentStep];
    return Comp ? <Comp onSaveAndContinue={goToNextStep} as="component" /> : <></>;
  };

  return (
    <div className="">
      <PageHeading heading={t("Get Started")} />
      <Modal
        open={open}
        modalWidth="rounded-md border border-gray-300 overflow-y-scroll"
        onClose={() => setOpen(false)}
        scroll={true}
      >
        <ModalSelectStore onClose={() => setOpen(false)} />
      </Modal>
      <Modal
        open={modalVideoOpen}
        modalWidth="rounded-md border border-gray-300"
        onClose={() => setModalVideoOpen(false)}
      >
        <div className="h-96 w-full">
          {/* <iframe
            className="video w-full h-80"
            sandbox="allow-same-origin allow-forms allow-popups allow-scripts allow-presentation"
            src={`https://youtube.com/embed/${videoId}?autoplay=0`}
          ></iframe> */}
          <div className="mt-2 space-x-4 p-3 text-center md:block">
            <button
              type="button"
              onClick={() => setModalVideoOpen(false)}
              className="justify-center rounded-sm border bg-white px-5 py-2 text-sm font-medium text-gray-600 shadow-sm hover:bg-gray-100 hover:shadow-md"
            >
              {t("Ok")}
            </button>
          </div>
        </div>
      </Modal>
      <div className="">
        <div className="get-started-layout flex min-h-0 flex-col gap-4 md:mb-0 md:flex-row md:items-stretch">
          {/* Mobile: horizontal steps */}
          {isMobile && (
            <div className="get-started-mobile-steps">
              <div className="get-started-mobile-steps-scroll">
                {mappedSteps.map((s, index) => {
                  const isActive = currentStep == s.activeKey;
                  const isCompleted =
                    Boolean(s.isCompleted) || index < currentStepIndex;
                  const isPrimary = isActive || isCompleted;
                  return (
                    <button
                      key={s.activeKey}
                      type="button"
                      className="get-started-mobile-step-btn"
                      onClick={() => setCurrentStep(s.activeKey)}
                    >
                      <span
                        className={`get-started-mobile-step-icon ${
                          isPrimary
                            ? "get-started-mobile-step-icon--active"
                            : ""
                        }`}
                      >
                        <AppIcon
                          name={s.icon}
                          className={
                            isPrimary ? "text-primary" : "text-gray-300"
                          }
                        />
                      </span>
                      <span
                        className={`get-started-mobile-step-title ${
                          isPrimary
                            ? "text-primary font-semibold"
                            : "text-gray-500"
                        }`}
                      >
                        {t(s.title)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Left: desktop vertical steps */}
          {!isMobile && (
            <aside className="get-started-steps-panel flex w-full shrink-0 flex-col md:w-1/5 md:min-h-0">
              <div className={stepsCardShell} aria-label="Onboarding steps">
                <div className="onboarding-steps-scroll min-h-0 flex-1 overflow-y-scroll overscroll-contain py-5 ps-5 pe-4">
                  <div className="flex flex-col gap-4">
                    {mappedSteps.map((s, index) => (
                      <div key={index} className="get-started-step-item">
                        <AppStep
                          icon={s.icon}
                          title={s.title}
                          description={s.content}
                          activeKey={s.activeKey}
                          onActive={(e) => setCurrentStep(e)}
                          active={currentStep == s.activeKey}
                          completed={
                            Boolean(s.isCompleted) || index < currentStepIndex
                          }
                          isLast={index === mappedSteps.length - 1}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          )}

          {/* Right: same height as left; AppContainer scrolls inside */}
          <div
            className={`get-started-content flex min-h-0 w-full min-w-0 flex-col md:w-4/5 ${onboardingPanelH}`}
          >
            {renderStepContent()}
          </div>
        </div>
      </div>
    </div>
  );
};
export default GetStarted;
